/**
 * Contextual editing: one catalogue of operations, three places a person meets them, one dialog.
 *
 * Correction 4 of `requirements-progressive-disclosure-261002.md` is blunt about what this replaces:
 * "'Edit' cannot be a 50-control form", and the ten always-visible fieldsets are "basically
 * `window.mage` rendered as HTML. That's exactly what we didn't want." The replacement it names is
 * not fewer operations — the machine API "can remain orthogonal and exhaustive" — but a different
 * PLACE for each: "the human UX should expose the operation where the user naturally encounters its
 * object."
 *
 * So the module is organised around one idea: **an operation is declared once, as an `EditAction`,
 * and every surface derives from that declaration.** The `+ Add` menu renders the additive actions;
 * the inspector renders the actions that apply to the current selection, prefilled with it; the
 * palette lists all of them with their preconditions; and the dialog builds the parameter form from
 * the same field list. Four surfaces that disagreed about what `add-relation` needs would be four
 * places to fix one defect — the failure the ten fieldsets already had, since each one spelled its
 * own option lists and its own refusals.
 *
 * **Why a typed catalogue rather than ten functions.** The old module was ten `addEventListener`
 * blocks, which is a shape that cannot answer a question the palette has to ask: *what can I do
 * right now, and if I cannot do this, why not?* A precondition stated as data is answerable; a
 * precondition implicit in which fieldset happens to be enabled is not. `EditAction.needs` is that
 * datum, and it is drawn from the same closed vocabulary the capability registry's navigation paths
 * use, so wave 1d can declare a path for each of these controls without re-deriving the condition.
 *
 * **What it does NOT own.** The mutation funnel stays in `edit-forms.ts`: one operation, one
 * envelope, one `Workspace.transact`, shared with the ask bar's Save and Retract (UX-I3). This
 * module decides which operation and with what arguments, and hands it over.
 *
 * **One dialog also carries a route INTO Learn, and that is the first of the two contextual routes
 * `requirements-learn-261002.md` asks for.** `add-model` is the only operation that asks for an
 * engineering question, so it is the only one where the registry's model types belong: the picker
 * offers them BY QUESTION — the author's framing throughout Learn is question first, not a
 * taxonomy of MAGE's internal types — and under them sits the "Not sure?" escape to the gallery.
 * The questions are `ModelType.question`, projected (`MODEL_TYPE_CHOICES`), never typed here. That
 * is UX-I9 one layer in from the Learn page: a hand-written question in this dialog would drift
 * away from what the kernel actually gates on the first time a registry entry moved, and the
 * drifted copy is the one a person creating a model would read.
 */
import { MODEL_TYPES, type ModelTypeId } from "../../engine/model-types.ts";
import { LEARN_PAGE } from "../../app/learn.ts";
import { fillSelect } from "../render-dom.ts";
import { elementValue, relationValue, resolveSelection } from "../view-model.ts";
import type {
  Choice, EditOptions, EditRequest, Selection, SelectionKind,
} from "../view-model.ts";
import { byId, mountIf, sel } from "./context.ts";
import type { ShellContext, ShellFrame, ShellRegion } from "./context.ts";
import type { EditOutcome, SubmitEdit } from "./edit-forms.ts";

/** The ten editing operations the transaction vocabulary offers a form for. */
export type EditForm =
  | "add-entity" | "add-state" | "add-relation" | "add-model" | "add-note"
  | "delete-element" | "delete-relation" | "delete-model"
  | "set-label" | "set-property";

/**
 * What must be true before an action can run.
 *
 * The same closed set `DESIGN-shell-261002.md` §2.2 gives `NavPrecondition`, narrowed to the five
 * members an editing action can need. Closed on purpose: the palette reports the reason an action
 * is unavailable, and a free-string reason is a sentence nobody checks.
 */
export type ActionPrecondition =
  | "loaded" | "selection:element" | "selection:relation" | "selection:model" | "selection:machine";

/** Which option list in `EditOptions` fills a choice field. */
export type OptionSource =
  | "entities" | "machines" | "models" | "relationTypes" | "domains" | "elements"
  | "labelled" | "relations" | "annotatable" | "noteKinds" | "value-kinds";

export interface Field {
  /** The key this field's value arrives under in `build`. Also the dialog input's id suffix. */
  readonly name: string;
  readonly label: string;
  readonly kind: "text" | "choice" | "check";
  /** Choice fields only: where the options come from. */
  readonly source?: OptionSource;
  /** Text fields only: the id of a shipped `<datalist>` offering type-ahead. */
  readonly datalist?: string;
  /** Choice fields only: offer an explicit "none", because a blank option and a missing one differ. */
  readonly optional?: true;
  readonly hint?: string;
}

/** What the dialog collected: text and choice values by field name, plus the ticked checkboxes. */
export interface FieldValues {
  readonly text: ReadonlyMap<string, string>;
  readonly checked: ReadonlySet<string>;
}

export interface EditAction {
  readonly form: EditForm;
  /** The dialog's heading, and the palette row's label. Imperative, and it names the object. */
  readonly label: string;
  /** The confirm button's text. A verb phrase, so the button says what pressing it does. */
  readonly verb: string;
  readonly needs: ActionPrecondition;
  /** Why the operation is shaped the way it is — the hint the dialog shows under its heading. */
  readonly hint: string;
  readonly fields: readonly Field[];
  readonly build: (values: FieldValues) => EditRequest;
  /**
   * The text field a model-type choice fills — and, by its presence, the switch that puts the
   * registry's model types in front of this operation's fields.
   *
   * ONE declaration rather than a boolean beside a field name: the picker writes into the field
   * this names, so the renderer never spells `"question"` itself and a test can check the name
   * against the action's own `fields` list. Absent on the other nine operations, which ask for no
   * engineering question and have nothing for a model type to narrow.
   */
  readonly questionField?: string;
}

/**
 * One row of the add-model picker: a registered model type, named and asked.
 *
 * **Projected, never written.** UX-I9's constraint on this surface is that the question beside a
 * model type come from the same registry entry the kernel's dispatch gate consults
 * (`ModelType.question`), so a new entry in `MODEL_TYPES` appears in the dialog with no edit here
 * and an edited question moves both. The field is carried BY IDENTITY from the registry — the
 * reason this is a projection of three fields rather than a list of three objects — because the
 * author's standing objection to a hand-maintained Learn surface is that it "will drift away from
 * what MAGE actually supports", and a dialog with the questions typed into it is that drift one
 * layer in from the Learn page.
 */
export interface ModelTypeChoice {
  readonly id: ModelTypeId;
  readonly label: string;
  readonly question: string;
}

/** The picker's rows, in registry order. The registry owns the count. */
export const MODEL_TYPE_CHOICES: readonly ModelTypeChoice[] =
  MODEL_TYPES.map((t) => ({ id: t.id, label: t.label, question: t.question }));

/**
 * Where "Not sure?" goes: the GALLERY, not one type's section.
 *
 * `requirements-learn-261002.md` sketches the escape as `[Learn about model types]` under a "Not
 * sure?", and the plural is the whole of the design — a reader who cannot tell which of three
 * questions they are asking is the one reader a per-type deep link misdirects. `learnHrefForType`
 * is the right call from the inspector, where the type is already known; here it is the wrong one.
 */
export const LEARN_GALLERY_HREF = LEARN_PAGE;

const text = (v: FieldValues, name: string): string => v.text.get(name) ?? "";

/**
 * The catalogue.
 *
 * The hints are the ones the ten fieldsets carried, kept verbatim where they still hold: each one
 * states a licensing fact a user cannot infer from the field names, and losing them in a move from
 * a form to a dialog would be the quiet half of a redesign regression.
 */
export const EDIT_ACTIONS: readonly EditAction[] = [
  {
    form: "add-entity",
    label: "Add an entity",
    verb: "Add the entity",
    needs: "loaded",
    hint: "Entities are the one identity namespace. The id is immutable once it exists.",
    fields: [
      { name: "id", label: "Id", kind: "text" },
      { name: "type", label: "Type (optional)", kind: "text" },
      { name: "label", label: "Label (optional)", kind: "text" },
    ],
    build: (v) => ({ form: "add-entity", id: text(v, "id"), type: text(v, "type"), label: text(v, "label") }),
  },
  {
    form: "add-state",
    label: "Add a state",
    verb: "Add the state",
    needs: "loaded",
    hint: "A control state of an existing machine. There is no operation for adding a machine itself.",
    fields: [
      { name: "machine", label: "Machine", kind: "choice", source: "machines" },
      { name: "state", label: "State name", kind: "text" },
    ],
    build: (v) => ({ form: "add-state", machine: text(v, "machine"), state: text(v, "state") }),
  },
  {
    form: "add-relation",
    label: "Connect two entities",
    verb: "Add the relation",
    needs: "loaded",
    // The licensing the capability registry asked for, and the reason the model field comes first:
    // the endpoint lists narrow to the chosen model's own entities, because `add-relation` does not
    // add an entity to a model, so an edge between two entities the model does not contain is an
    // edge no view of that model will ever draw.
    hint: "A relation is asserted BY a model. The endpoints offered are the ones that model "
      + "contains — a relation between entities it does not contain is one no view of it will draw.",
    fields: [
      { name: "model", label: "Model", kind: "choice", source: "models" },
      { name: "from", label: "From", kind: "choice", source: "entities" },
      { name: "to", label: "To", kind: "choice", source: "entities" },
      { name: "type", label: "Relation type", kind: "choice", source: "relationTypes" },
    ],
    build: (v) => ({
      form: "add-relation", model: text(v, "model"), from: text(v, "from"),
      to: text(v, "to"), type: text(v, "type"),
    }),
  },
  {
    form: "add-model",
    label: "Add a model",
    verb: "Add the model",
    needs: "loaded",
    hint: "A model states one engineering question. The question is required here even though the "
      + "operation does not carry one (UX-I4): a model whose purpose nobody wrote is a diagram.",
    fields: [
      { name: "id", label: "Id", kind: "text" },
      { name: "label", label: "Label (optional)", kind: "text" },
      { name: "question", label: "The engineering question this model answers", kind: "text" },
      {
        name: "entities", label: "Entities (optional, separated by commas)", kind: "text",
        datalist: "entity-ids",
      },
    ],
    build: (v) => ({
      form: "add-model", id: text(v, "id"), label: text(v, "label"),
      question: text(v, "question"), entities: text(v, "entities"),
    }),
    // The one operation that asks for an engineering question, so the one dialog where the
    // registry's model types belong: the Learn requirement's picker offers them BY QUESTION, and
    // choosing one fills this field. Nothing else about the operation changes — `add-model` still
    // declares a purposeful model and still sends `add-model` plus `set-purpose`.
    questionField: "question",
  },
  {
    form: "add-note",
    label: "Attach a note",
    verb: "Attach the note",
    needs: "loaded",
    hint: "A note is context, not a constraint: it does not advance the model's revision and it "
      + "cannot invalidate a pending transaction. Entities, models and relations can carry one.",
    fields: [
      { name: "target", label: "Attach to", kind: "choice", source: "annotatable" },
      { name: "kind", label: "Kind", kind: "choice", source: "noteKinds" },
      { name: "text", label: "Note", kind: "text" },
    ],
    build: (v) => ({ form: "add-note", target: text(v, "target"), kind: text(v, "kind"), text: text(v, "text") }),
  },
  {
    form: "set-label",
    label: "Rename",
    verb: "Change the label",
    needs: "loaded",
    hint: "The label, not the id — an id is immutable. The list says which namespace each choice "
      + "came from, because the operation takes a bare id and an id naming both an entity and a "
      + "machine is genuinely ambiguous.",
    fields: [
      { name: "id", label: "Element", kind: "choice", source: "labelled" },
      { name: "label", label: "New label", kind: "text" },
    ],
    build: (v) => ({ form: "set-label", id: text(v, "id"), label: text(v, "label") }),
  },
  {
    form: "set-property",
    label: "Set a property",
    verb: "Set the property",
    needs: "loaded",
    hint: "Properties are typed. The kind is chosen explicitly because 30 and \"30\" are different "
      + "values and guessing which one was meant is how a comparison silently stops working.",
    fields: [
      { name: "id", label: "Entity", kind: "choice", source: "entities" },
      { name: "name", label: "Property name", kind: "text", datalist: "property-names" },
      { name: "valueKind", label: "Kind", kind: "choice", source: "value-kinds" },
      { name: "value", label: "Value", kind: "text" },
      { name: "domain", label: "Domain (optional)", kind: "choice", source: "domains", optional: true },
      { name: "unset", label: "clear this property instead of setting it", kind: "check" },
    ],
    build: (v) => {
      const kind = text(v, "valueKind");
      return {
        form: "set-property", id: text(v, "id"), name: text(v, "name"), value: text(v, "value"),
        valueKind: kind === "integer" || kind === "boolean" ? kind : "string",
        domain: text(v, "domain"), unset: v.checked.has("unset"),
      };
    },
  },
  {
    form: "delete-element",
    label: "Delete an element",
    verb: "Delete the element",
    needs: "loaded",
    hint: "Refused while anything still refers to it, unless you ask for the references to go too.",
    fields: [
      { name: "element", label: "Element", kind: "choice", source: "elements" },
      { name: "cascade", label: "also delete everything that refers to it", kind: "check" },
    ],
    build: (v) => ({
      form: "delete-element", element: text(v, "element"), cascade: v.checked.has("cascade"),
    }),
  },
  {
    form: "delete-relation",
    label: "Remove a relation",
    verb: "Remove the relation",
    needs: "loaded",
    hint: "Relations have no ids of their own, so one is addressed by the model that asserts it "
      + "together with its endpoints and type.",
    fields: [{ name: "relation", label: "Relation", kind: "choice", source: "relations" }],
    build: (v) => ({ form: "delete-relation", relation: text(v, "relation") }),
  },
  {
    form: "delete-model",
    label: "Remove a model",
    verb: "Remove the model",
    needs: "loaded",
    hint: "The model and its relations. The entities it mentions are the system's, not the "
      + "model's, and they stay.",
    fields: [{ name: "model", label: "Model", kind: "choice", source: "models" }],
    build: (v) => ({ form: "delete-model", model: text(v, "model") }),
  },
];

const action = (form: EditForm): EditAction => {
  const found = EDIT_ACTIONS.find((a) => a.form === form);
  if (found === undefined) throw new Error(`no EditAction declares '${form}'`);
  return found;
};

/** The five additive operations, in the order the `+ Add` menu offers them. */
export const ADD_MENU: readonly EditForm[] =
  ["add-entity", "add-state", "add-relation", "add-model", "add-note"];

/**
 * One contextual action, as a selection makes it available.
 *
 * `label` is re-worded per context rather than reused from the catalogue, because the catalogue's
 * label names the operation ("Connect two entities") and the inspector's names the act on THIS
 * object ("Connect Analytics to…"). `prefill` is what the selection already determines, which is
 * the substance of correction 4: the user encountered the object, so the dialog should not ask them
 * to find it again in a select.
 */
export interface ContextualAction {
  readonly form: EditForm;
  readonly label: string;
  readonly prefill: ReadonlyMap<string, string>;
  /** The stable button in `#inspector-actions` that offers it. */
  readonly element: string;
}

const prefill = (...pairs: readonly (readonly [string, string])[]): ReadonlyMap<string, string> =>
  new Map(pairs);

/**
 * What a selection can be edited with, derived from the selection and the system.
 *
 * Pure, and deliberately the ONE place the per-kind sets are written: the inspector renders this,
 * the palette's availability reads it, and `test/shell-edit.test.ts` asserts it against systems
 * loaded from the shipped examples rather than against a transcript of what the pane says today.
 *
 * **Two entries correction 4 asks for are absent, and the kernel is why.** "Selected model → Edit
 * purpose" needs an operation that rewrites a model's engineering question, and "Selected relation
 * → Edit" needs one that changes a relation in place. The transaction vocabulary has neither
 * (`EditRequest` is the whole of it), and `set-label` reaches a model's LABEL, not its question. A
 * disabled button or a dialog that quietly deleted and re-added would both be worse than the
 * absence: one is a control that cannot work, the other is an edit the user did not ask for.
 */
export function contextualActions(selected: Selection): readonly ContextualAction[] {
  switch (selected.kind) {
    case "entity": {
      // The ID, from the ref — never the wire string. This case used to read `selection[0]`, so a
      // tree selection (`entity:analytics`) offered "Rename entity:analytics…" and prefilled that
      // as the `id` of the edit, while the agent's bare `analytics` offered the right one. The
      // model and machine cases stripped their prefix by hand and this one forgot; a typed ref
      // leaves nothing to forget.
      const id = selected.id;
      return [
        { form: "set-label", label: `Rename ${id}…`, prefill: prefill(["id", id]), element: "act-rename" },
        {
          form: "set-property", label: `Set a property on ${id}…`,
          prefill: prefill(["id", id]), element: "act-set-property",
        },
        {
          form: "add-relation", label: `Connect ${id} to…`,
          prefill: prefill(["from", id]), element: "act-connect",
        },
        {
          form: "add-note", label: `Attach a note to ${id}…`,
          prefill: prefill(["target", elementValue(selected)]), element: "act-note",
        },
        {
          form: "delete-element", label: `Delete ${id}…`,
          prefill: prefill(["element", elementValue(selected)]), element: "act-delete-element",
        },
      ];
    }
    case "relation": {
      const value = relationValue(selected.ref);
      return [
        {
          form: "add-note", label: "Attach a note to this relation…",
          prefill: prefill(["target", value]), element: "act-note",
        },
        {
          form: "delete-relation", label: "Delete this relation…",
          prefill: prefill(["relation", value]), element: "act-delete-relation",
        },
      ];
    }
    case "model": {
      const id = selected.id;
      return [
        { form: "set-label", label: `Rename ${id}…`, prefill: prefill(["id", id]), element: "act-rename" },
        {
          form: "add-relation", label: `Connect two entities in ${id}…`,
          prefill: prefill(["model", id]), element: "act-connect",
        },
        {
          form: "add-note", label: `Attach a note to ${id}…`,
          prefill: prefill(["target", `model:${id}`]), element: "act-note",
        },
        {
          form: "delete-model", label: `Delete the model ${id}…`,
          prefill: prefill(["model", id]), element: "act-delete-model",
        },
      ];
    }
    case "machine":
      return [
        {
          form: "set-label", label: `Rename ${selected.id}…`,
          prefill: prefill(["id", selected.id]), element: "act-rename",
        },
      ];
    case "state":
      return [
        {
          form: "delete-element", label: "Delete this state…",
          prefill: prefill(["element", elementValue(selected)]), element: "act-delete-element",
        },
      ];
    case "none":
    case "unresolved":
      return [];
  }
}

/** Is an action's precondition met by the current selection? The palette's availability predicate. */
export function satisfies(need: ActionPrecondition, loaded: boolean, kind: SelectionKind): boolean {
  if (!loaded) return false;
  switch (need) {
    case "loaded": return true;
    case "selection:element": return kind === "entity" || kind === "state";
    case "selection:relation": return kind === "relation";
    case "selection:model": return kind === "model";
    case "selection:machine": return kind === "machine";
  }
}

// --------------------------------------------------------------------------------------------
// The dialog
// --------------------------------------------------------------------------------------------

/** What opening a dialog needs: which operation, and what the selection already determined. */
export type OpenDialog = (form: EditForm, prefill?: ReadonlyMap<string, string>) => void;

const KIND_CHOICES: readonly Choice[] = [
  { value: "string", label: "text" },
  { value: "integer", label: "integer" },
  { value: "boolean", label: "boolean" },
];

/**
 * The options a choice field offers, from the one `EditOptions` the view model computed this paint.
 *
 * `add-relation`'s endpoints are narrowed by the chosen model, which is why this takes the current
 * model value rather than reading `options.entities` for every entity field: the narrowing is the
 * licensing fact the old form spent a function on, and it belongs to the field list, not to a
 * change listener bolted beside it.
 */
function optionsFor(source: OptionSource, options: EditOptions, model: string): readonly Choice[] {
  if (source === "value-kinds") return KIND_CHOICES;
  if (source === "entities") return options.modelEntities.get(model) ?? options.entities;
  return options[source];
}

export interface EditDialogs extends ShellRegion {
  readonly open: OpenDialog;
}

/**
 * Mount the dialog, the `+ Add` menu, the inspector's action bar — and, until wave 2d re-derives
 * six keyboard drives, the ten legacy fieldsets.
 */
export function mountEditDialogs(ctx: ShellContext, submitEdit: SubmitEdit): EditDialogs {
  const dialog = byId<HTMLDialogElement>("edit-dialog");
  const heading = byId("edit-dialog-h");
  const hint = byId("edit-dialog-hint");
  const fieldHost = byId("edit-dialog-fields");
  const typesHost = byId("edit-dialog-types");
  const typeRows = byId("edit-dialog-type-rows");
  // The destination comes from `src/app/learn.ts`, exactly as the header entry's does, so "where
  // Learn lives" stays one value. The markup ships the same string — the escape works before the
  // bundle evaluates — which is what makes this assignment a check of one fact rather than two
  // hand-typed copies agreeing.
  byId<HTMLAnchorElement>("edit-dialog-learn").href = LEARN_GALLERY_HREF;
  const problem = byId("edit-dialog-problem");
  const confirm = byId<HTMLButtonElement>("edit-dialog-confirm");
  const addMenu = byId<HTMLDetailsElement>("add-menu");
  const actionBar = byId("inspector-actions");
  const actionHint = byId("inspector-actions-hint");

  /** The options of the last paint. A dialog opens between paints, so it reads the current frame's. */
  let options: EditOptions | null = null;
  /** What is open, so the confirm button knows which operation it is submitting. */
  let current: EditAction | null = null;

  const fieldId = (a: EditAction, f: Field): string => `edit-dialog-${a.form}-${f.name}`;

  /**
   * Render one operation's parameter form.
   *
   * Built per open rather than shipped, because ten operations' fields are genuinely ten different
   * forms and shipping all of them is the 50-control page correction 4 refuses. The shell's
   * "mount once" rule is about LISTENERS, and the only listeners here are the two shipped buttons
   * plus one delegated `change` for the model narrowing — nothing is bound to generated markup.
   */
  function renderFields(a: EditAction, filled: ReadonlyMap<string, string>): void {
    const opts = options;
    const model = filled.get("model") ?? (opts === null ? "" : opts.models[0]?.value ?? "");
    const frag = document.createDocumentFragment();
    for (const f of a.fields) {
      const id = fieldId(a, f);
      if (f.kind === "check") {
        const wrap = document.createElement("div");
        wrap.className = "check";
        const box = document.createElement("input");
        box.type = "checkbox";
        box.id = id;
        const label = document.createElement("label");
        label.htmlFor = id;
        label.textContent = f.label;
        wrap.append(box, label);
        frag.append(wrap);
        continue;
      }
      const label = document.createElement("label");
      label.className = "field";
      label.htmlFor = id;
      const span = document.createElement("span");
      span.textContent = f.label;
      label.append(span);
      if (f.kind === "choice") {
        const select = document.createElement("select");
        select.id = id;
        select.dataset["field"] = f.name;
        const choices = opts === null ? [] : optionsFor(f.source ?? "entities", opts, model);
        fillSelect(select, f.optional === true ? [{ value: "", label: "none" }, ...choices] : choices);
        const want = filled.get(f.name);
        if (want !== undefined) select.value = want;
        label.append(select);
      } else {
        const box = document.createElement("input");
        box.type = "text";
        box.id = id;
        box.autocomplete = "off";
        if (f.datalist !== undefined) box.setAttribute("list", f.datalist);
        box.value = filled.get(f.name) ?? "";
        label.append(box);
      }
      frag.append(label);
    }
    fieldHost.replaceChildren(frag);
  }

  /** Read the generated form back. The dialog's fields are the only source; nothing is remembered. */
  function collect(a: EditAction): FieldValues {
    const values = new Map<string, string>();
    const checked = new Set<string>();
    for (const f of a.fields) {
      const node = document.getElementById(fieldId(a, f));
      if (node === null) continue;
      if (f.kind === "check") {
        if ((node as HTMLInputElement).checked) checked.add(f.name);
      } else {
        values.set(f.name, (node as HTMLInputElement | HTMLSelectElement).value);
      }
    }
    return { text: values, checked };
  }

  /**
   * The model-type picker, built ONCE at mount.
   *
   * Not per open, because `MODEL_TYPES` is a compile-time registry and nothing in a session can
   * change it — unlike the parameter fields, which differ per operation and carry a prefill. So
   * this is shipped-shaped content in a generated host, and the one listener below binds to the
   * host rather than to the three buttons.
   *
   * Each row is ONE button whose accessible name is the type followed by its question, which is
   * the sketch's own row shape. A second control per row linking to that type's Learn section was
   * considered and dropped: the inspector already offers the per-type route from the object, and
   * six controls in a dialog whose job is four fields crowds the surface the escape below answers
   * in one.
   */
  function renderTypeChoices(): void {
    const frag = document.createDocumentFragment();
    for (const c of MODEL_TYPE_CHOICES) {
      const pick = document.createElement("button");
      pick.type = "button";
      pick.className = "type-choice";
      // The question travels on the element that was clicked, so the handler needs no closure over
      // the row it was built in — the same arrangement the action bar's prefill map has, for the
      // same reason. It is a registry string, not a user-typed id, so there is nothing to escape.
      pick.dataset["question"] = c.question;
      const name = document.createElement("span");
      name.className = "type-choice-label";
      name.textContent = c.label;
      const asks = document.createElement("span");
      asks.className = "type-choice-question";
      asks.textContent = c.question;
      pick.append(name, asks);
      frag.append(pick);
    }
    typeRows.replaceChildren(frag);
  }
  renderTypeChoices();

  /**
   * Choosing a question. The field is the action's own declared one, so nothing here spells it.
   *
   * It FILLS rather than commits: the question stays editable and the user still presses the
   * confirm button, because a model's purpose is usually narrower than the type's generic question
   * and a picker that submitted would teach the opposite of what the field exists to teach.
   */
  typeRows.addEventListener("click", (event) => {
    const a = current;
    const from = event.target;
    if (a === null || a.questionField === undefined || !(from instanceof Element)) return;
    const pick = from.closest<HTMLButtonElement>("button[data-question]");
    const question = pick?.dataset["question"];
    if (question === undefined) return;
    const field = a.fields.find((f) => f.name === a.questionField);
    if (field === undefined) {
      // Unreachable while the node tier holds `questionField` to a name in `fields`; the throw is
      // for the day someone renames the field and leaves the declaration behind, when a loud
      // failure beats a picker that silently does nothing.
      throw new Error(`'${a.form}' declares questionField '${a.questionField}', which is not one of its fields`);
    }
    const box = document.getElementById(fieldId(a, field));
    if (!(box instanceof HTMLInputElement)) return;
    box.value = question;
    box.focus();
    ctx.announce(`The question is now “${question}”. Edit it to say what this model in particular asks.`);
  });

  const open: OpenDialog = (form, filled) => {
    const a = action(form);
    current = a;
    heading.textContent = a.label;
    hint.textContent = a.hint;
    problem.replaceChildren();
    confirm.textContent = a.verb;
    renderFields(a, filled ?? new Map());
    // Hidden rather than absent for the other nine: `hidden` takes the picker out of the tab order
    // and the accessibility tree together, which is the one property a class that hides does not
    // have.
    mountIf(typesHost, a.questionField !== undefined);
    addMenu.open = false;
    dialog.showModal();
    // The platform traps focus and returns it on close; it does not CHOOSE where focus lands or say
    // that anything happened. Both are ours: the first field, so a keyboard user types immediately,
    // and one polite sentence, because opening a modal is exactly the consequential change
    // FR-A11Y-3 asks to be narrated.
    fieldHost.querySelector<HTMLElement>("input, select")?.focus();
    // The picker is announced because it stands BEFORE the fields in the tab order and focus starts
    // after it: a keyboard user who is not told would have to shift-tab backwards to discover three
    // controls and a Learn link. The count comes from the registry, like the rows.
    const offered = a.questionField === undefined
      ? ""
      : ` ${MODEL_TYPE_CHOICES.length} kinds of model are offered first, each by the question it `
        + "answers, with a Learn link for choosing between them.";
    ctx.announce(`${a.label}.${offered} ${a.fields.length} field(s), then ${a.verb}. `
      + "Escape closes without editing.");
  };

  /** Show a refusal inside the dialog, and keep it open with everything the user typed intact. */
  function refuse(outcome: Extract<EditOutcome, { ok: false }>): void {
    const p = document.createElement("p");
    p.className = "refusal";
    p.textContent = outcome.problem;
    const nodes: HTMLElement[] = [p];
    if (outcome.findings.length > 0) {
      const list = document.createElement("ul");
      for (const f of outcome.findings) {
        const item = document.createElement("li");
        item.textContent = f.message;
        list.append(item);
      }
      nodes.push(list);
    }
    problem.replaceChildren(...nodes);
    // Not focused: moving the caret onto a message a screen reader is already reading is the focus
    // theft FR-A11Y-3 names. The funnel announces the refusal through `#live`.
  }

  confirm.addEventListener("click", () => {
    const a = current;
    if (a === null) return;
    const outcome = submitEdit(a.build(collect(a)));
    if (outcome.ok) {
      dialog.close();
      return;
    }
    refuse(outcome);
  });

  byId("edit-dialog-cancel").addEventListener("click", () => dialog.close());

  // The model narrowing, as ONE delegated listener on the host rather than a handler bound to a
  // select this paint created. `add-relation`'s endpoints depend on the chosen model, so changing
  // the model re-renders the form — keeping whatever is already in it, because re-rendering a form
  // a user has half-filled and silently emptying it is worse than not narrowing at all.
  fieldHost.addEventListener("change", (event) => {
    const a = current;
    const from = event.target;
    if (a === null || !(from instanceof HTMLSelectElement) || from.dataset["field"] !== "model") return;
    renderFields(a, collect(a).text);
  });

  dialog.addEventListener("close", () => {
    current = null;
    // Focus returns to the opener by the platform. Saying so is the point of the announcement: a
    // dismissed dialog that says nothing reads as a control that did nothing.
    ctx.announce("Dialog closed.");
    // And a repaint, because closing this dialog can be what RELEASES another surface to open. An
    // edit held for review (G3) lands on a branch while this modal is still up, and the review
    // surface refuses to open over it — two modals stacked is a focus ordering the platform does
    // not define. Its opening condition is therefore re-evaluated the moment this one closes,
    // declaratively on the next paint rather than on a timer.
    ctx.repaint();
  });

  for (const form of ADD_MENU) {
    byId(`add-menu-${form.replace("add-", "")}`).addEventListener("click", () => open(form));
  }

  /**
   * The inspector's action bar: seven stable buttons, each carrying whichever contextual action
   * claims its element this paint.
   *
   * The handler reads what `paint` last recorded rather than closing over the frame it was bound in,
   * which is the inspector's own arrangement and for the same reason: a listener that captured a
   * selection would act on the one that was current when the page loaded.
   *
   * **It is a map and not a dataset round-trip, and the deleted separator is why that matters.** The
   * prefill used to be flattened onto the button as `key=value` pairs joined by a NUL and read back
   * by splitting on it. A control-byte gate flagged the raw separator in the source; the first
   * repair DELETED it, which turns `split(SEP)` into `split("")` and empties every dialog field —
   * and the whole suite stayed green, because nothing exercised the round trip. Escaping the byte
   * fixed the instance. NOT SERIALIZING AT ALL removes the hazard: a `ReadonlyMap<string, string>`
   * travels from `paint` to the handler through a `let` with exactly the lifetime `options` and
   * `current` above already have — latest paint wins, nothing is captured — so there is no separator
   * to choose, no escaping to get right, and no id a user could type that breaks the parse. Which
   * answers the standing question about the separator itself: the keys are the catalogue's own field
   * names, but the VALUES are entity and model ids a person types into `add-entity`, so NO printable
   * separator was safe and the control byte was load-bearing for as long as the encoding existed.
   * The dialog takes the map directly, as the canvas context menu already did (`workspace.ts` calls
   * `openDialog(action.form, action.prefill)`) — the second site adopting one shape, not a third one.
   *
   * The palette keeps its `data-form`, and that is not an inconsistency: its rows are GENERATED per
   * render and read by one delegated listener, so each row must declare which operation it offers.
   * These seven are shipped markup with a listener each, so the attribute carried nothing the
   * closure could not.
   */
  const actionButtons = ["act-rename", "act-set-property", "act-connect", "act-note",
    "act-delete-element", "act-delete-relation", "act-delete-model"]
    .map((id) => byId<HTMLButtonElement>(id));

  /** Which contextual action claims each action-bar element, as of the last paint. */
  let offeredByElement: ReadonlyMap<string, ContextualAction> = new Map();

  for (const button of actionButtons) {
    button.addEventListener("click", () => {
      const offered = offeredByElement.get(button.id);
      if (offered === undefined) return;
      open(offered.form, offered.prefill);
    });
  }

  // ------------------------------------------------------------------------------------------
  // The legacy fieldsets, bound here and nowhere else.
  //
  // DRAINED, not deleted, and the distinction is a pin rather than a preference: six §19 keyboard
  // drives in `test/browser/a11y/keyboard.test.mjs` type into these fields by id, and a field
  // inside a closed `<dialog>` is not focusable. That file belongs to the wave that generates
  // drives from declared paths (§9, wave 2d). What wave 2a could do is make this the ONLY code
  // that knows the fieldsets exist, so the deletion is this block plus the markup — and make each
  // one submit through the same `EditAction.build` the dialog uses, so there is one author for what
  // `set-property` means rather than two that drift.
  // ------------------------------------------------------------------------------------------

  /** Field id in the legacy markup for one catalogue field, where the two spellings differ. */
  const LEGACY_IDS: Readonly<Record<string, string>> = {
    "add-state.state": "add-state-id",
    "add-relation.model": "add-relation-model",
    "add-note.text": "add-note-text",
    "set-label.id": "set-label-target",
    "set-label.label": "set-label-value",
    "set-property.id": "set-property-target",
    "set-property.valueKind": "set-property-kind",
    "delete-element.element": "delete-element-target",
    "delete-relation.relation": "delete-relation-target",
    "delete-model.model": "delete-model-target",
    "add-note.target": "add-note-target",
  };

  const legacyId = (a: EditAction, f: Field): string =>
    LEGACY_IDS[`${a.form}.${f.name}`] ?? `${a.form}-${f.name}`;

  function collectLegacy(a: EditAction): FieldValues {
    const values = new Map<string, string>();
    const checked = new Set<string>();
    for (const f of a.fields) {
      const node = document.getElementById(legacyId(a, f));
      if (node === null) continue;
      if (f.kind === "check") {
        if ((node as HTMLInputElement).checked) checked.add(f.name);
      } else {
        values.set(f.name, (node as HTMLInputElement | HTMLSelectElement).value);
      }
    }
    return { text: values, checked };
  }

  /** The ten `*-go` buttons, by the operation each submits. The verb half of the id varies. */
  const LEGACY_BUTTONS: Readonly<Record<EditForm, string>> = {
    "add-entity": "add-entity-go",
    "add-state": "add-state-go",
    "add-relation": "add-relation-go",
    "add-model": "add-model-go",
    "add-note": "add-note-go",
    "set-label": "set-label-go",
    "set-property": "set-property-go",
    "delete-element": "delete-element-go",
    "delete-relation": "delete-relation-go",
    "delete-model": "delete-model-go",
  };

  for (const a of EDIT_ACTIONS) {
    byId(LEGACY_BUTTONS[a.form]).addEventListener("click", () => submitEdit(a.build(collectLegacy(a))));
  }

  /** The legacy selects, refilled per paint. The option lists are the catalogue's, not a second list. */
  const legacySelects = EDIT_ACTIONS.flatMap((a) =>
    a.fields
      .filter((f) => f.kind === "choice")
      .map((f) => ({ field: f, node: sel(legacyId(a, f)) })));

  // `edit-mode` is NOT in this list any more: correction 8 deleted the branch-choosing fieldset,
  // and the review surface decides where a change lands.
  const legacyFieldsets = [
    "form-add-entity", "form-add-state", "form-delete-element",
    "form-add-relation", "form-delete-relation", "form-set-label", "form-set-property",
    "form-add-model", "form-delete-model", "form-add-note",
  ].map((id) => byId<HTMLFieldSetElement>(id));

  // The legacy relation endpoints narrow on the model select, exactly as they did before the move.
  const legacyRelationModel = sel("add-relation-model");
  const refreshLegacyEndpoints = (): void => {
    const opts = options;
    if (opts === null) return;
    const inModel = opts.modelEntities.get(legacyRelationModel.value) ?? opts.entities;
    fillSelect(sel("add-relation-from"), inModel);
    fillSelect(sel("add-relation-to"), inModel);
  };
  legacyRelationModel.addEventListener("change", () => refreshLegacyEndpoints());

  function fillDatalist(root: HTMLElement, values: readonly string[]): void {
    root.replaceChildren(...values.map((value) => {
      const option = document.createElement("option");
      option.value = value;
      return option;
    }));
  }

  return {
    open,
    paint: (frame: ShellFrame) => {
      options = frame.vm.edit;
      const loaded = frame.state.loaded;

      // The `+ Add` menu and the action bar belong to regions the root mounts; their own hosts are
      // shown or hidden with the surface they sit in, so only availability is decided here.
      mountIf(addMenu, loaded);
      mountIf(actionBar, loaded);
      byId<HTMLButtonElement>("palette-open").disabled = !loaded;

      const contextual = loaded
        ? contextualActions(resolveSelection(frame.state.system, ctx.viewState.selection[0]))
        : [];
      offeredByElement = new Map(contextual.map((c) => [c.element, c]));
      for (const button of actionButtons) {
        const offered = offeredByElement.get(button.id);
        button.disabled = offered === undefined;
        // The label is the only thing the DOM needs now: `disabled` says whether the button is on
        // offer, and WHICH action it offers is read out of the map above. Behaviour here is
        // unchanged from the dataset version, stale label included — a button that goes back to
        // disabled keeps the last selection's wording rather than returning to the generic one the
        // markup ships. That is pre-existing and recorded in the as-built rather than fixed inside a
        // change about the prefill encoding.
        if (offered !== undefined) button.textContent = offered.label;
      }
      // The bar says WHY it is empty, because seven disabled buttons with no sentence beside them
      // read as a broken region rather than as a surface waiting for a selection.
      //
      // The empty wording said a second time what the pane's own empty sentence says, and then
      // explained the division of operations between here and `+ Add`. The division moved to Learn
      // ("How the workbench is laid out"); what is left is the one thing a reader needs at this
      // spot, which is that the bar is waiting for a selection and additive work is elsewhere.
      actionHint.textContent = contextual.length > 0
        ? "These act on what is selected. Each opens a small dialog for just that operation."
        : "Waiting for a selection. Additive operations are under + Add.";

      for (const { field, node } of legacySelects) {
        const choices = optionsFor(field.source ?? "entities", frame.vm.edit, "");
        fillSelect(node, field.optional === true
          ? [{ value: "", label: "none" }, ...choices]
          : choices);
      }
      // The relation endpoints are refilled again below, narrowed to the chosen model: the loop
      // above offers every entity, which is the right default and the wrong final state.
      fillDatalist(byId("property-names"), frame.vm.edit.propertyNames);
      fillDatalist(byId("entity-ids"), frame.vm.edit.entityIds);
      refreshLegacyEndpoints();
      for (const f of legacyFieldsets) f.disabled = !loaded;
    },
  };
}
